import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * Reusable "Top Movers" panel. Used in 4 places now — Banking Rules,
 * Menu Items, Suppliers, Customers — each with the same UX language.
 *
 * Props:
 *   movers — array of { id, name, current, previous, delta, direction }.
 *            The caller normalises whatever underlying entity (rule,
 *            menu item, supplier, customer) into this common shape.
 *   title  — header label, e.g. "Top movers · last 7 days vs prior 7 days"
 *   subtitle — small italic hint, e.g. "spot misfiring rules early"
 *   tone   — 'violet' | 'emerald' | 'sky' | 'amber' to differentiate
 *            sections visually
 *   unit   — what the count represents, e.g. "fires", "sold", "SAR"
 *   testidPrefix — e.g. "rules" → outer testid `{prefix}-top-movers`,
 *                  per-card `{prefix}-mover-{id}`
 *   formatValue — optional fn(num) → string for amount-based movers
 *                 (overrides default integer rendering for currency)
 */
export function MoversPanel({
  movers,
  title,
  subtitle,
  tone = 'violet',
  unit = 'fires',
  testidPrefix = 'movers',
  formatValue,
}) {
  if (!movers || movers.length === 0) return null;

  // Match toneMap shape to PageInfoCallout for visual consistency.
  const toneMap = {
    violet: { border: 'border-violet-200', bg: 'bg-violet-50/30 dark:bg-violet-950/10', text: 'text-violet-800 dark:text-violet-200' },
    emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50/30 dark:bg-emerald-950/10', text: 'text-emerald-800 dark:text-emerald-200' },
    sky: { border: 'border-sky-200', bg: 'bg-sky-50/30 dark:bg-sky-950/10', text: 'text-sky-800 dark:text-sky-200' },
    amber: { border: 'border-amber-200', bg: 'bg-amber-50/30 dark:bg-amber-950/10', text: 'text-amber-800 dark:text-amber-200' },
  };
  const t = toneMap[tone] || toneMap.violet;

  const fmt = formatValue || ((v) => v);

  return (
    <Card className={`${t.border} ${t.bg}`} data-testid={`${testidPrefix}-top-movers`}>
      <CardHeader className="pb-2">
        <CardTitle className={`text-xs font-outfit flex items-center gap-2 ${t.text}`}>
          <TrendingUp size={13} /> {title}
          {subtitle ? (
            <span className="text-[10px] font-normal text-muted-foreground ml-1">· {subtitle}</span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {movers.map((m, idx) => {
            const Icon = m.direction === 'up' ? TrendingUp : m.direction === 'down' ? TrendingDown : Minus;
            const cardTone = m.direction === 'up'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
              : m.direction === 'down'
                ? 'border-rose-300 bg-rose-50 text-rose-800'
                : 'border-stone-300 bg-stone-50 text-stone-700';
            // Guard against backend rows with missing IDs (null/undefined)
            // — fall back to the array index so React's key uniqueness
            // invariant is always satisfied even on dirty data.
            const key = m.id ?? `mover-${idx}`;
            return (
              <div key={key} className={`rounded-lg border px-2.5 py-2 ${cardTone}`} data-testid={`${testidPrefix}-mover-${m.id ?? idx}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold leading-tight">
                  <Icon size={12} className="shrink-0" />
                  <span className="truncate" title={m.name}>{m.name}</span>
                </div>
                <div className="mt-1 text-[10px] tabular-nums opacity-90">
                  {fmt(m.previous)} → <strong>{fmt(m.current)}</strong> {unit}
                  <span className="ml-1.5 font-semibold">
                    {m.delta > 0 ? '+' : ''}{fmt(m.delta)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
